'use client'

import { Coin } from '@repo/studio-design-system-ui-coin'
import { motion, useReducedMotion } from 'framer-motion'
import { Button } from '../button/button'
import { IconAutoAwesome } from '../icons'
import type { ButtonProps } from '../button/button'
import { tw } from 'utils/classnames'
import { formatNumberToNumericalAbreviation } from 'utils/number'

/** Typical 4-digit coin chip width; used while `price` is still fetching. */
const PENDING_PRICE_CHIP_WIDTH_PX = 73.5

export type GenerateButtonProps = Omit<ButtonProps, 'variant' | 'iconLeft' | 'iconRight'> & {
  /**
   * Coin cost for the next generation. Like Studio's GenerateButton: a resting chip sizes the
   * control and a hover chip overlays the generate icon (`opacity-0` until hover). While `price`
   * is still fetching (`undefined`), the slot holds 73.5px and animates to the real chip width (or
   * to 0 when the action is free). While disabled, only the resting chip stays mounted so width
   * does not jump. A cost of `0` renders the plain control (vectorize, background removal).
   */
  price?: number
}

function formatCoinAmount(coins: number) {
  return coins >= 10_000 ? formatNumberToNumericalAbreviation(coins) : coins
}

function CoinCost({ coins }: { coins: number | string }) {
  return (
    <span
      className={tw(
        'inline-flex translate-x-0.5 items-center gap-1 rounded-sm bg-cf-theme-green-100 px-2 py-0.5',
        'text-12 font-semibold leading-none text-cf-theme-green-1000',
      )}
    >
      <Coin appearance="monochrome" size="sm" />
      {coins}
    </span>
  )
}

function RestingPriceSlot({
  coinsFormatted,
  isPricePending,
}: {
  coinsFormatted: number | string | null
  isPricePending: boolean
}) {
  const reduceMotion = useReducedMotion()
  const width = isPricePending ? PENDING_PRICE_CHIP_WIDTH_PX : coinsFormatted === null ? 0 : 'auto'

  return (
    <motion.span
      aria-hidden
      className="invisible col-start-1 row-start-1 inline-flex overflow-hidden justify-end"
      initial={false}
      animate={{ width }}
      transition={reduceMotion ? { duration: 0 } : { duration: 0.2, ease: 'easeOut' }}
    >
      {coinsFormatted === null ? null : <CoinCost coins={coinsFormatted} />}
    </motion.span>
  )
}

export function GenerateButton({
  fullWidth = true,
  className,
  children,
  price,
  loading,
  disabled,
  ...props
}: GenerateButtonProps) {
  const isLoading = Boolean(loading)
  const isPricePending = price === undefined
  const coinsFormatted = price !== undefined && price > 0 ? formatCoinAmount(price) : null
  const shouldShowHoverPrice = coinsFormatted !== null && !isLoading && !disabled

  return (
    <Button
      {...props}
      className={tw('group/generate-button justify-between', className)}
      variant="primary-green"
      fullWidth={fullWidth}
      loading={loading}
      disabled={disabled}
      iconRight={
        <span className="relative inline-grid items-center justify-items-end">
          <IconAutoAwesome className="col-start-1 row-start-1" />
          <RestingPriceSlot coinsFormatted={coinsFormatted} isPricePending={isPricePending} />
          {shouldShowHoverPrice ? (
            <span
              className={tw(
                'pointer-events-none absolute inset-y-0 right-0 flex items-center px-1 justify-end',
                'opacity-0 transition-opacity group-hover/generate-button:opacity-100',
              )}
            >
              <CoinCost coins={coinsFormatted} />
            </span>
          ) : null}
        </span>
      }
    >
      {children}
    </Button>
  )
}
